// frontend/src/components/GroupCreatorModal.jsx

import React, { useState, useEffect, useRef } from 'react'

import { useTranslation } from '../i18n'
import { getJWTSession } from '../utils/useJWTSession'
import { setStoredSession } from '../hooks/useHashSession'
import NotificationChannelSelector from './NotificationChannelSelector' // Importa el componente

import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/min';
import 'react-phone-number-input/style.css';
import es from 'react-phone-number-input/locale/es.json';
import en from 'react-phone-number-input/locale/en.json';


export default function GroupCreatorModal({ onClose, onSave }) {
  const { _, lang } = useTranslation()
  const inputRef = useRef(null)

  const [form, setForm] = useState({
    group_name: '',
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    whatsapp: false,
  })

  // Nuevo estado para los flags de notificación
  const [notificationFlags, setNotificationFlags] = useState({
    notify_by_email: true,
    notify_by_whatsapp: false,
    cc_leader: false,
  })

  const [formValid, setFormValid] = useState(false)
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Autofocus
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus()
  }, [])

  // ESC para cerrar
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const validateForm = (data) => {
    // Validar los campos obligatorios
    const isBasicValid = data.group_name && data.owner_name && data.owner_email;

    // La validación del teléfono ahora es más explícita
    // Si hay un teléfono, debe ser un número válido según la librería
    const isPhoneValid = !data.owner_phone || isValidPhoneNumber(data.owner_phone);

    return isBasicValid && isPhoneValid;
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const updated = {
      ...form,
      [name]: type === 'checkbox' ? checked : value,
    };
    setForm(updated);
    setFormValid(validateForm(updated));
  };

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const token = getJWTSession()
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/group`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...form,
          ...notificationFlags, // Agrega los flags del selector de canal al payload
        }),
      })

      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || _('Server error'))
      }

      const data = await res.json()
      const hash = data.group.main_hash
      setStoredSession(hash, 'leader')
      onSave(data.group)
      requestAnimationFrame(() => {
        setTimeout(() => onClose(), 0);
      });
    } catch (err) {
      setError(_('Error creating group'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className="modal-box"
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <h2 className="modal-title mb-4" id="modal-title">
          {_('Create new group')}
        </h2>

        <div className="flex flex-col gap-y-4">
          <div>
            <label className="form-label">{_('Group Name')}</label>
            <input
              ref={inputRef}
              type="text"
              name="group_name"
              className="input-inline w-full"
              value={form.group_name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">{_('Responsible Name')}</label>
              <input
                type="text"
                name="owner_name"
                className="input-inline w-full"
                value={form.owner_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="form-label">{_('Responsible Phone')}</label>
              <PhoneInput
                placeholder={_("Enter phone number")}
                defaultCountry="DO" // País por defecto: República Dominicana
                value={form.owner_phone}
                onChange={(value) => {
                  // Valida el número de teléfono en tiempo real
                  const phoneIsValid = value ? isValidPhoneNumber(value) : false;
                  setIsPhoneValid(phoneIsValid); // <-- Actualiza el estado específico del teléfono

                  // Se mantiene la lógica existente para el formulario general
                  const updated = { ...form, owner_phone: value };
                  setForm(updated);
                  setFormValid(validateForm(updated));
                }}
                className="phone-input-custom"
                countrySelectProps={{ className: "country-select-dark-colors" }}
                labels={lang === 'es' ? es : en}
              />
            </div>
          </div>

          <div>
            <label className="form-label">{_('Responsible Email')}</label>
            <input
              type="email"
              name="owner_email"
              className="input-inline w-full"
              value={form.owner_email}
              onChange={handleChange}
              required
            />
          </div>

          {/* Renderiza el selector de canal en lugar del checkbox manual */}
          <div className="mt-2">
            <NotificationChannelSelector
              contactEmail={form.owner_email}
              contactPhone={form.owner_phone}
              onChange={setNotificationFlags}
              defaultEmail={true}
              defaultWhatsapp={false}
              defaultCcLeader={false}
            />
          </div>

          {error && <p className="error-text">{error}</p>}
        </div>

        <div className="form-actions mt-6">
          <button type="button" className="button-secondary" onClick={onClose}>
            {_('Cancel')}
          </button>
          <button
            type="submit"
            className="button-primary"
            disabled={!formValid || loading}
          >
            {_('Create')}
          </button>
        </div>
      </form>
    </div>
  )
}